import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { BrowserModule, Title } from '@angular/platform-browser';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { MaterialModule } from './essentials/modules/material/material.module';
import { LibUiModule } from '@fcp/lib-ui';
import { ServiceWorkerModule } from '@angular/service-worker';

import { AppInterceptor } from './app.interceptor';

/* components */
import { AppComponent } from './app.component';
import { HeaderComponent } from './shared/header/header.component';
import { FooterComponent } from './shared/footer/footer.component';
import { HomeComponent } from './home/home.component';
import { LogoComponent } from './shared/logo/logo.component';
import { BadgesComponent } from './shared/footer/badges/badges.component';
import { CompanyComponent } from './shared/footer/company/company.component';
import { HelpdeskComponent } from './shared/footer/helpdesk/helpdesk.component';
import { PagesComponent } from './pages/pages.component';
import { FilterPensionComponent } from './filter/pension/pension.component';
import { FilterHealthComponent } from './filter/health/health.component';
import { PrevButtonComponent } from './filter/prev-button/prev-button.component';
import { FilterProspecComponent } from './filter/prospec/prospec.component';
import { SearchComponent } from './shared/header/search/search.component';
import { NotFoundComponent } from './not-found/not-found.component';
import {
  CleanPanelComponent,
  OverlayComponent
} from './shared/overlay/overlay.component';
import { NewsHighlightComponent } from './news-highlight/news-highlight.component';
import { PublishingHighlightComponent } from './publishing-highlight/publishing-highlight.component';
import { DetailHighlightComponent } from './detail-highlight/detail-highlight.component';
import { MenuHamburgerComponent } from './shared/header/menu-hamburger/menu-hamburger.component';
import { ExclusiveAreaComponent } from './shared/exclusive-area/exclusive-area.component';
import { GoogleAnalyticsComponent } from './essentials/google-analytics/google-analytics.component';
import { PagesPreviewComponent } from './pages/preview/preview.component';
import { SurveyComponent } from './survey/survey.component';
import { RatingComponent } from './rating/rating.component';
import { BulletComponent } from './survey/bullet/bullet.component';
import { FloatingLinksComponent } from './floating-links/floating-links.component';
import { BannerContainerComponent } from './shared/components/banner-container/banner-container.component';
import { AnchorBannerContainerComponent } from './shared/components/anchor-banner-container/anchor-banner-container.component';
import { NewsCarouselContainerComponent } from './shared/components/news-carousel-container/news-carousel-container.component';
import { PrevServiceContainerComponent } from './shared/components/prev-service-container/prev-service-container.component';
import { ShowcaseBenefitsContainerComponent } from './shared/components/showcase-benefits-container/showcase-benefits-container.component';
import { NewsletterContainerComponent } from './shared/components/newsletter-container/newsletter-container.component';
import { PrevServiceGroupContainerComponent } from './shared/components/prev-service-group-container/prev-service-group-container.component';
import { ShowcaseFeaturesContainerComponent } from './shared/components/showcase-features-container/showcase-features-container.component';
import { CountersContainerComponent } from './shared/components/counters-container/counters-container.component';
import { FaqContainerComponent } from './shared/components/faq-container/faq-container.component';
import { SectionTaxContainerComponent } from './shared/components/section-tax-container/section-tax-container.component';
import { CardInfoContainerComponent } from './shared/components/card-info-container/card-info-container.container';
import { CardIconeTituloLinkContainerComponent } from './shared/components/card-icone-titulo-link-container/card-icone-titulo-link-container.component';
import { SectionIconContainerComponent } from './shared/components/section-icon-container/section-icon-container.component';
import { SectionCertificationContainerComponent } from './shared/components/section-certification-container/section-certification-container.component';
import { SectionSocialContainerComponent } from './shared/components/section-social-container/section-social-container.component';
import { PagesIncContainerComponent } from './shared/components/pages-inc-container/pages-inc-container.component';
import { SectionCardSaudeContainerComponent } from './shared/components/section-card-saude-container/section-card-saude-container.component';
import { SectionChartContainerComponent } from './shared/components/section-chart-container/section-chart-container.component';
import { NewBannerContainerComponent } from './shared/components/new-banner-container/new-banner-container.component';
import { DynamicTablePreviewComponent } from './pages/preview/dynamic-table-preview/dynamic-table-preview.component';
import { InvestmentReportContainerComponent } from './shared/components/investment-report-container/investment-report-container.component';
import { IconeTituloContainerComponent } from './shared/components/icone-titulo-container/icone-titulo-container.component';
import { DescricaoCorFundoContainerComponent } from './shared/components/descricao-cor-fundo-container/descricao-cor-fundo-container.component';
import { ImagemTituloDescricaoContainerComponent } from './shared/components/imagem-titulo-descricao-container/imagem-titulo-descricao-container.component';
import { SectionImagemContainerComponent } from './shared/components/section-imagem-container/section-imagem-container.component';
import { TituloSubtituloCorFundoContainerComponent } from './shared/components/titulo-subtitulo-cor-fundo-container/titulo-subtitulo-cor-fundo-container.component';
import { LinkInternoVoltarContainerComponent } from './shared/components/link-interno-voltar-container/link-interno-voltar-container.component';
import { UnidadesClinicaContainerComponent } from './shared/components/unidades-clinica-container/unidades-clinica-container.component';
import { BannerTextoGrandeContainerComponent } from './shared/components/banner-texto-grande-container/banner-texto-grande-container.component';

/* services */
import { HttpClientService } from './essentials/http/http-client.service';
import { ApplicationInsightsService } from './essentials/application-insights/application-insights.service';
import { CookieService } from 'ngx-cookie-service';
import { MicroFrontEndService } from './essentials/micro-front-end/micro-front-end.service';
import { CMSAuthService } from './essentials/cms/auth/cms-auth.service';
import { CMSSessionService } from './essentials/cms/session/cms-session.service';
import { CMSContentService } from './essentials/cms/content/cms-content.service';

/* pipe */
import { PipeModule } from './essentials/modules/pipe/pipe.module';
import { SlugPipe } from './essentials/modules/pipe/slug/slug.pipe';

/* swiper */
import { SwiperConfigInterface } from 'ngx-swiper-wrapper';
import { SwiperModule } from 'ngx-swiper-wrapper';
import { SWIPER_CONFIG } from 'ngx-swiper-wrapper';

import { environment } from '../environments/environment';

const DEFAULT_SWIPER_CONFIG: SwiperConfigInterface = {
  direction: 'horizontal',
  slidesPerView: 'auto'
};

const SwiperConfig = {
  provide: SWIPER_CONFIG,
  useValue: DEFAULT_SWIPER_CONFIG
};

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    FooterComponent,
    HomeComponent,
    LogoComponent,
    BadgesComponent,
    CompanyComponent,
    HelpdeskComponent,
    PagesComponent,
    FilterPensionComponent,
    FilterHealthComponent,
    FilterProspecComponent,
    PrevButtonComponent,
    SearchComponent,
    NewsHighlightComponent,
    NotFoundComponent,
    CleanPanelComponent,
    OverlayComponent,
    PublishingHighlightComponent,
    DetailHighlightComponent,
    MenuHamburgerComponent,
    ExclusiveAreaComponent,
    GoogleAnalyticsComponent,
    PagesPreviewComponent,
    SurveyComponent,
    RatingComponent,
    BulletComponent,
    FloatingLinksComponent,
    BannerContainerComponent,
    AnchorBannerContainerComponent,
    NewsCarouselContainerComponent,
    PrevServiceContainerComponent,
    ShowcaseBenefitsContainerComponent,
    NewsletterContainerComponent,
    PrevServiceGroupContainerComponent,
    ShowcaseFeaturesContainerComponent,
    CountersContainerComponent,
    FaqContainerComponent,
    CardInfoContainerComponent,
    CardIconeTituloLinkContainerComponent,
    SectionIconContainerComponent,
    SectionTaxContainerComponent,
    SectionCertificationContainerComponent,
    SectionSocialContainerComponent,
    PagesIncContainerComponent,
    SectionCardSaudeContainerComponent,
    SectionChartContainerComponent,
    DynamicTablePreviewComponent,
    NewBannerContainerComponent,
    InvestmentReportContainerComponent,
    IconeTituloContainerComponent,
    DescricaoCorFundoContainerComponent,
    ImagemTituloDescricaoContainerComponent,
    SectionImagemContainerComponent,
    TituloSubtituloCorFundoContainerComponent,
    LinkInternoVoltarContainerComponent,
    UnidadesClinicaContainerComponent,
    BannerTextoGrandeContainerComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    CommonModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    PipeModule,
    MaterialModule,
    SwiperModule,
    LibUiModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production
    })
  ],
  providers: [
    HttpClientService,
    CookieService,
    MicroFrontEndService,
    CMSAuthService,
    CMSSessionService,
    CMSContentService,
    ApplicationInsightsService,
    Title,
    SwiperConfig,
    SlugPipe,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AppInterceptor,
      multi: true
    }
  ],
  entryComponents: [OverlayComponent, CleanPanelComponent],
  bootstrap: [AppComponent]
})
export class AppModule {
  constructor() {}
}
